import {
  useRouteBodyEditorSelection,
  useRouteBodyState,
  useRouteBodyRef,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import {
  MorphemeDescriptionRenderer,
  useThemedMarkdownStyle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.description";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { useUpsertMorphemeNote } from "@-/dev.improve.haniverse.lib.morpheme.note.update";
import { queryOptions, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useEffect, useRef } from "react";
import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { Pressable, View } from "@-/com.npmjs.react-native";
import {
  EnrichedMarkdownTextInput,
  type EnrichedMarkdownTextInputInstance,
} from "@-/com.npmjs.react-native-enriched-markdown";
import { withUniwind } from "@-/com.npmjs.uniwind";

const UniwindEnrichedMarkdownTextInput = withUniwind(EnrichedMarkdownTextInput);

/** Starts a custom note where the note itself has no room to offer one. */
export function MorphemeMnemonicsMeaningViewAddNoteButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      accessibilityLabel="Add Custom Note"
      accessibilityRole="button"
      className="min-h-11 items-center justify-center px-3"
      onPress={onPress}
      testID="add-custom-note"
    >
      <Text className="text-center text-sm text-muted-foreground underline">+ Add Custom Note</Text>
    </Pressable>
  );
}

/**
 * Personal notes with a route-persistent inline editor, shared by cards and the Learn view.
 * `inline` notes sit under a heading without their own divider or padding, and an empty inline
 * note renders nothing: its caller places the add button.
 */
export function MorphemeMnemonicsMeaningViewNotesSection({
  editing,
  inline = false,
  morpheme,
  onContentPresenceChange,
  showAddButton = false,
}: {
  editing?: { isEditing: boolean; onFinish: () => void; onStart?: () => void };
  inline?: boolean;
  morpheme: string;
  onContentPresenceChange?: (hasContent: boolean) => void;
  showAddButton?: boolean;
}) {
  const [localIsEditing, setLocalIsEditing] = useRouteBodyState("notes-editing", false);
  const isEditing = editing?.isEditing ?? localIsEditing;
  const startEditing = () => {
    setLocalIsEditing(true);
    editing?.onStart?.();
  };
  const stopEditing = () => {
    setLocalIsEditing(false);
    editing?.onFinish();
  };
  const morphemeNoteQueryFn = actorQueries(import.meta.location).morphemeNote.findFirst.queryFn;
  const getMorphemeNoteQueryKey = actorQueries(
    import.meta.location,
  ).morphemeNote.findFirst.useGetQueryKey({});
  const { data: morphemeNote, refetch } = useQuery(
    queryOptions({
      queryKey: getMorphemeNoteQueryKey({ where: { morpheme } }),
      queryFn: morphemeNoteQueryFn,
    }),
  );
  const upsertMorphemeNote = useUpsertMorphemeNote();
  const markdownStyle = useThemedMarkdownStyle();
  const draftRef = useRouteBodyRef("notes-draft", "");
  const wasEditingRef = useRouteBodyRef("notes-was-editing", false);
  const inputRef = useRef<EnrichedMarkdownTextInputInstance | null>(null);
  const editorSelection = useRouteBodyEditorSelection(inputRef, "notes-editor");

  const content = morphemeNote?.text ?? "";
  const hasContent = content.trim() !== "";

  useEffect(() => {
    onContentPresenceChange?.(hasContent);
  }, [hasContent, onContentPresenceChange]);

  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      draftRef.current = content;
    }
    wasEditingRef.current = isEditing;
  }, [content, isEditing, draftRef, wasEditingRef]);

  useEffect(() => {
    if (isEditing) {
      return;
    }
    void inputRef.current?.setValue(content);
  }, [morpheme, content, isEditing]);

  const handleSave = () => {
    upsertMorphemeNote.mutate(
      { morpheme, referenceNote: draftRef.current },
      {
        onSuccess: () => {
          void refetch();
          stopEditing();
          void inputRef.current?.blur();
        },
      },
    );
  };

  const handleCancel = () => {
    draftRef.current = content;
    void inputRef.current?.setValue(content);
    void inputRef.current?.blur();
    stopEditing();
  };

  return (
    <>
      {isEditing || hasContent ? (
        <View
          className={inline ? "gap-2" : "gap-2 border-t border-border/60 p-3"}
          testID="meaning-notes"
        >
          {/* Personal notes are free text the learner wrote: masked in session replays. */}
          {isEditing ? (
            <PostHogMaskView>
              <UniwindEnrichedMarkdownTextInput
                {...editorSelection}
                accessibilityLabel="Personal notes"
                className="min-h-16 w-full text-sm leading-6"
                defaultValue={isEditing && wasEditingRef.current ? draftRef.current : content}
                editable={!upsertMorphemeNote.isPending}
                key={morpheme}
                markdownStyle={markdownStyle}
                onChangeMarkdown={(markdown) => {
                  draftRef.current = markdown;
                }}
                ref={inputRef}
                testID="custom-notes-input"
              />
            </PostHogMaskView>
          ) : (
            <View testID="custom-notes-content">
              <PostHogMaskView>
                <MorphemeDescriptionRenderer content={content} size="sm" />
              </PostHogMaskView>
              {showAddButton ? (
                <Pressable
                  accessibilityLabel="Edit custom note"
                  accessibilityRole="button"
                  className="min-h-8 justify-center self-start"
                  onPress={startEditing}
                >
                  <Text className="text-xs text-muted-foreground">Edit custom note</Text>
                </Pressable>
              ) : null}
            </View>
          )}
          {isEditing ? (
            <>
              {upsertMorphemeNote.error ? (
                <Text accessibilityRole="alert" className="text-sm text-destructive">
                  Could not save notes. {upsertMorphemeNote.error.message}
                </Text>
              ) : null}
              <View className="flex-row justify-end gap-1">
                <Button
                  accessibilityLabel="Cancel note"
                  disabled={upsertMorphemeNote.isPending}
                  onPress={handleCancel}
                  size="sm"
                  variant="ghost"
                >
                  <Text>Cancel</Text>
                </Button>
                <Button
                  accessibilityLabel="Save note"
                  disabled={upsertMorphemeNote.isPending}
                  onPress={handleSave}
                  size="sm"
                  variant="ghost"
                >
                  <Text>{upsertMorphemeNote.isPending ? "Saving…" : "Save"}</Text>
                </Button>
              </View>
            </>
          ) : null}
        </View>
      ) : showAddButton && !inline ? (
        <MorphemeMnemonicsMeaningViewAddNoteButton onPress={startEditing} />
      ) : null}
    </>
  );
}
